import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { detectCapabilities, supportsWebGl } from "@/runtime/capability";
import { acquireCameraStream, isPermissionDenied, releaseCameraStream } from "@/runtime/cameraViewer";
import { reportRuntimeError, subscribeRuntimeErrors, type RuntimeErrorRecord } from "@/runtime/runtimeErrors";
import { DEFAULT_PLACEMENT, type PlacementPose, type RuntimeMode, type RuntimePhase } from "@/runtime/types";
import { setWebXrDomOverlayActive } from "@/runtime/webxrDomOverlay";
import { WebXRRuntime } from "@/runtime/WebXRRuntime";
import { getDemo, preloadDemo } from "@/demos/registry";
import type { DemoId, QualityTier } from "@/demos/types";
import { resolveScreen } from "@/journey/journeySteps";
import type { JourneyScreen, PendingAccess } from "@/journey/types";
import { initialQualityTier } from "@/scene/qualityPolicy";
import { xrStore } from "@/scene/SceneStage";

interface JourneyOptions {
  activeDemoId: DemoId;
  showNotice: (message: string) => void;
  videoRef: RefObject<HTMLVideoElement | null>;
}

export interface JourneyController {
  screen: JourneyScreen;
  webglAvailable: boolean;
  mode: RuntimeMode;
  phase: RuntimePhase;
  pendingAccess: PendingAccess;
  placement: PlacementPose | null;
  placementProgress: number;
  quality: QualityTier;
  resetEpoch: number;
  error: string | null;
  runtimeError: RuntimeErrorRecord | null;
  /** 1 · Entry -> Start */
  start: () => Promise<void>;
  /** 3 · Permission -> Allow */
  grantAccess: () => Promise<void>;
  /** 3 · Permission -> Not now, A/B · Continue */
  continueIn3D: (message?: string) => void;
  /** B · Try again */
  retryAccess: () => Promise<void>;
  /** C · Resume */
  resumeTracking: () => void;
  /** 5 · Reposition */
  reposition: () => void;
  /** 5/6/7 · Reset */
  resetDemo: () => void;
  setQuality: (quality: QualityTier) => void;
  setPlacementProgress: (progress: number) => void;
  setRuntimeError: (record: RuntimeErrorRecord | null) => void;
  acceptPlacement: (pose: PlacementPose) => void;
  handleWebglContextLost: () => void;
}

export function useJourney({ activeDemoId, showNotice, videoRef }: JourneyOptions): JourneyController {
  const [webglAvailable] = useState(supportsWebGl);
  const [started, setStarted] = useState(false);
  const [mode, setMode] = useState<RuntimeMode>("non-ar");
  const [phase, setPhase] = useState<RuntimePhase>("idle");
  const [pendingAccess, setPendingAccess] = useState<PendingAccess>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [noArSupport, setNoArSupport] = useState(false);
  const [placement, setPlacement] = useState<PlacementPose | null>(null);
  const [placementProgress, setPlacementProgress] = useState(0);
  const [quality, setQuality] = useState<QualityTier>(initialQualityTier);
  const [resetEpoch, setResetEpoch] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [runtimeError, setRuntimeError] = useState<RuntimeErrorRecord | null>(null);

  const cameraStream = useRef<MediaStream | null>(null);
  const mountedRef = useRef(true);
  const requestRef = useRef(0);
  const webxrActiveRef = useRef(false);
  const webglRecoveryRef = useRef(false);
  const [webxrRuntime] = useState(() => new WebXRRuntime(xrStore));

  useEffect(() => subscribeRuntimeErrors(setRuntimeError), []);
  useEffect(() => setWebXrDomOverlayActive(mode === "webxr"), [mode]);

  const stopCamera = useCallback(() => {
    releaseCameraStream(cameraStream.current, videoRef.current);
    cameraStream.current = null;
  }, [videoRef]);

  const continueIn3D = useCallback((message?: string) => {
    requestRef.current += 1;
    stopCamera();
    setPendingAccess(null);
    setPermissionDenied(false);
    setNoArSupport(false);
    setMode("non-ar");
    setPlacement(DEFAULT_PLACEMENT);
    setPlacementProgress(1);
    setPhase("placed");
    setError(null);
    setStarted(true);
    if (message) showNotice(message);
  }, [showNotice, stopCamera]);

  useEffect(() => webxrRuntime.subscribe((snapshot) => {
    if (!webxrActiveRef.current) return;
    if (snapshot.phase === "stopped") {
      webxrActiveRef.current = false;
      setMode("non-ar");
      setPlacement(DEFAULT_PLACEMENT);
      setPlacementProgress(1);
      setPhase("placed");
      showNotice("AR session ended · 3D viewer active");
      return;
    }
    setPhase(snapshot.phase);
    setPlacement(snapshot.placement);
    setPlacementProgress(snapshot.placement ? 1 : 0);
    if (snapshot.phase === "placed" && snapshot.placementMode === "fixed") {
      showNotice("Surface not found · fixed AR placement");
    }
  }), [showNotice, webxrRuntime]);

  /** Wireframe 1 -> 2 -> (3 | A). Decides which mode to offer, then commits. */
  const start = useCallback(async () => {
    const requestId = ++requestRef.current;
    const isActive = () => mountedRef.current && requestRef.current === requestId;
    webglRecoveryRef.current = false;
    setStarted(true);
    setError(null);
    setPermissionDenied(false);
    setNoArSupport(false);
    setPhase("checking");

    // Warm the first demo in the background rather than awaiting it. Capability
    // must not queue behind a multi-megabyte model: the visitor should learn
    // whether AR is possible in milliseconds, and the scene's Suspense boundary
    // covers the asset either way. The permission explainer is the dead time
    // this download is meant to hide behind.
    void preloadDemo(activeDemoId).catch((cause: unknown) => {
      if (!isActive()) return;
      reportRuntimeError(cause, `${getDemo(activeDemoId).title} asset load failed`);
      setError("The experience could not be loaded. Check your connection and try again.");
      setPhase("error");
    });

    let preferredMode: RuntimeMode = "non-ar";
    try {
      preferredMode = (await detectCapabilities()).preferredMode;
    } catch (cause) {
      reportRuntimeError(cause, "Capability detection failed");
    }
    if (!isActive()) return;

    if (preferredMode === "non-ar") {
      // Wireframe fallback A.
      setNoArSupport(true);
      setMode("non-ar");
      setPlacement(DEFAULT_PLACEMENT);
      setPlacementProgress(1);
      setPhase("placed");
      return;
    }

    // Wireframe screen 3. Stop here and explain before touching the camera —
    // and, critically, so that entering AR happens inside the visitor's own
    // click rather than after an await chain that has consumed the gesture.
    setMode(preferredMode);
    setPendingAccess(preferredMode === "webxr" ? "webxr" : "camera");
    setPhase("requesting-permission");
  }, [activeDemoId]);

  const startCameraViewer = useCallback(async (recoveryMessage?: string) => {
    const requestId = ++requestRef.current;
    const isActive = () => mountedRef.current && requestRef.current === requestId;
    setPhase("requesting-permission");
    try {
      const stream = await acquireCameraStream(videoRef.current);
      if (!isActive()) {
        releaseCameraStream(stream, videoRef.current);
        return;
      }
      cameraStream.current = stream;
      setPendingAccess(null);
      setPermissionDenied(false);
      setMode("camera-viewer");
      setPlacement(DEFAULT_PLACEMENT);
      setPlacementProgress(1);
      setPhase("placed");
      setError(null);
      if (recoveryMessage) showNotice(recoveryMessage);
    } catch (cause) {
      stopCamera();
      if (!isActive()) return;
      if (isPermissionDenied(cause)) {
        // Wireframe fallback B.
        setPendingAccess(null);
        setPermissionDenied(true);
        setPhase("stopped");
        return;
      }
      reportRuntimeError(cause, "Camera startup failed");
      continueIn3D("Camera unavailable · 3D viewer active");
    }
  }, [continueIn3D, showNotice, stopCamera, videoRef]);

  const startWebXr = useCallback(async () => {
    const requestId = ++requestRef.current;
    const isActive = () => mountedRef.current && requestRef.current === requestId;
    setMode("webxr");
    webxrActiveRef.current = true;
    try {
      await webxrRuntime.start();
    } catch (cause) {
      webxrActiveRef.current = false;
      await webxrRuntime.stop("error");
      if (!isActive()) return;
      if (isPermissionDenied(cause)) {
        setPendingAccess(null);
        setPermissionDenied(true);
        setPhase("stopped");
        return;
      }
      reportRuntimeError(cause, "AR session failed to start");
      await startCameraViewer("AR unavailable · camera preview active");
      return;
    }
    if (!isActive()) {
      webxrActiveRef.current = false;
      await webxrRuntime.stop("unmount");
      return;
    }
    setPendingAccess(null);
  }, [startCameraViewer, webxrRuntime]);

  /** Wireframe 3 · Allow. Runs inside the click, so user activation survives. */
  const grantAccess = useCallback(async () => {
    if (pendingAccess === "webxr") await startWebXr();
    else await startCameraViewer();
  }, [pendingAccess, startCameraViewer, startWebXr]);

  /** Wireframe B · Try again. */
  const retryAccess = useCallback(async () => {
    setPermissionDenied(false);
    if (mode === "webxr") await startWebXr();
    else await startCameraViewer();
  }, [mode, startCameraViewer, startWebXr]);

  /** Wireframe C · Resume. */
  const resumeTracking = useCallback(() => {
    if (mode === "webxr") webxrRuntime.resume();
    else setPhase("placed");
  }, [mode, webxrRuntime]);

  const acceptPlacement = useCallback((pose: PlacementPose) => {
    if (mode === "webxr") webxrRuntime.acceptPlacement(pose);
    setPlacement(pose);
    setPlacementProgress(1);
    setPhase("placed");
  }, [mode, webxrRuntime]);

  /** Wireframe 5 · Reposition. */
  const reposition = useCallback(() => {
    if (mode === "webxr") {
      void webxrRuntime.reposition();
      return;
    }
    setPlacement(DEFAULT_PLACEMENT);
    setResetEpoch((value) => value + 1);
  }, [mode, webxrRuntime]);

  /** Wireframe 5/6/7 · Reset — restores the canonical view of the current demo. */
  const resetDemo = useCallback(() => setResetEpoch((value) => value + 1), []);

  const handleWebglContextLost = useCallback(() => {
    if (!mountedRef.current || webglRecoveryRef.current || mode !== "webxr") return;
    webglRecoveryRef.current = true;
    requestRef.current += 1;
    setPhase("recovering");
    console.warn("WebGL context lost during the AR session; returning to the 3D viewer.");
    webxrActiveRef.current = false;
    void webxrRuntime.stop("error").finally(() => {
      if (mountedRef.current) continueIn3D("XR rendering unavailable · 3D viewer active");
    });
  }, [continueIn3D, mode, webxrRuntime]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestRef.current += 1;
      stopCamera();
      webxrActiveRef.current = false;
      void webxrRuntime.stop("unmount");
    };
  }, [stopCamera, webxrRuntime]);

  const screen = resolveScreen({
    webglAvailable, started, mode, phase, pendingAccess, permissionDenied, noArSupport,
  });

  return {
    screen, webglAvailable, mode, phase, pendingAccess, placement, placementProgress,
    quality, resetEpoch, error, runtimeError,
    start, grantAccess, continueIn3D, retryAccess, resumeTracking, reposition, resetDemo,
    setQuality, setPlacementProgress, setRuntimeError, acceptPlacement, handleWebglContextLost,
  };
}
